import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "./i18n";
import { MAJOR_ARCANA_IMAGES, CARD_NAME_TO_NUMBER } from "./tarotDesigns";
import { MINOR_ARCANA } from "./minorArcana";

// Build minor arcana lookup
const MINOR_LOOKUP = {};
Object.values(MINOR_ARCANA).forEach(card => {
  if (card.name_ru) MINOR_LOOKUP[card.name_ru] = card.image;
  if (card.name) MINOR_LOOKUP[card.name] = card.image;
});

// Card back design - mystical pattern
const CARD_BACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='248' viewBox='0 0 160 248'%3E%3Crect width='160' height='248' rx='12' fill='%23120e1a'/%3E%3Crect x='6' y='6' width='148' height='236' rx='9' fill='none' stroke='%23D4AF37' stroke-width='1.5' opacity='0.6'/%3E%3Crect x='14' y='14' width='132' height='220' rx='6' fill='none' stroke='%23D4AF37' stroke-width='0.5' opacity='0.3'/%3E%3Ccircle cx='80' cy='124' r='40' fill='none' stroke='%23D4AF37' stroke-width='1' opacity='0.4'/%3E%3Ccircle cx='80' cy='124' r='25' fill='none' stroke='%239333EA' stroke-width='0.8' opacity='0.5'/%3E%3Cpath d='M80 84 L80 164 M40 124 L120 124' stroke='%23D4AF37' stroke-width='0.5' opacity='0.2'/%3E%3Cpath d='M80 94 L85 109 L100 109 L88 118 L92 133 L80 124 L68 133 L72 118 L60 109 L75 109 Z' fill='%23D4AF37' opacity='0.3'/%3E%3C/svg%3E";

const TOTAL_FAN_CARDS = 15;
const PHASES = { SHUFFLING: 'shuffling', FAN: 'fan', PICKING: 'picking', REVEALING: 'revealing' };

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const TarotCardPicker = ({ onComplete, apiPromise }) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [phase, setPhase] = useState(PHASES.SHUFFLING);
  const [pickedIndices, setPickedIndices] = useState([]);
  const [apiResult, setApiResult] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [revealedCards, setRevealedCards] = useState([]);
  const [showInterpretation, setShowInterpretation] = useState(false);

  const positionLabels = [
    t('divination.past') || 'Past',
    t('divination.present') || 'Present', 
    t('divination.future') || 'Future'
  ];

  // Listen for API result
  useEffect(() => {
    if (apiPromise) {
      apiPromise
        .then(res => setApiResult(res.data))
        .catch(err => setApiError(err));
    }
  }, [apiPromise]);

  // Phase transitions
  useEffect(() => {
    if (phase === PHASES.SHUFFLING) {
      const timer = setTimeout(() => setPhase(PHASES.FAN), 2400);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  // Auto-transition to fan -> picking
  useEffect(() => {
    if (phase === PHASES.FAN) {
      const timer = setTimeout(() => setPhase(PHASES.PICKING), 800);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  // Handle card pick
  const handlePick = useCallback((index) => {
    if (phase !== PHASES.PICKING || pickedIndices.includes(index) || pickedIndices.length >= 3) return;
    setPickedIndices(prev => [...prev, index]);
  }, [phase, pickedIndices]);

  // When 3 cards picked, start reveal
  useEffect(() => {
    if (pickedIndices.length === 3 && phase === PHASES.PICKING) {
      const timer = setTimeout(() => setPhase(PHASES.REVEALING), 600);
      return () => clearTimeout(timer);
    }
  }, [pickedIndices, phase]);

  // Reveal cards one by one
  useEffect(() => {
    if (phase !== PHASES.REVEALING) return;
    const timers = [];
    for (let i = 0; i < 3; i++) {
      timers.push(setTimeout(() => {
        setRevealedCards(prev => [...prev, i]);
      }, i * 800 + 500));
    }
    // Show interpretation after all cards revealed
    timers.push(setTimeout(() => {
      setShowInterpretation(true);
    }, 3200));
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  // Complete: pass result to parent
  const handleComplete = useCallback(() => {
    if (apiResult) {
      onComplete(apiResult);
    } else if (apiError) {
      onComplete(null, apiError);
    }
  }, [apiResult, apiError, onComplete]);

  // Get card art URL for revealed card
  const getCardArt = (cardIndex) => {
    if (!apiResult?.cards?.[cardIndex]) return null;
    const card = apiResult.cards[cardIndex];
    // Try major arcana
    const arcanaNum = CARD_NAME_TO_NUMBER[card.name_ru] ?? CARD_NAME_TO_NUMBER[card.name] ?? null;
    if (arcanaNum !== null && MAJOR_ARCANA_IMAGES[arcanaNum]) {
      return MAJOR_ARCANA_IMAGES[arcanaNum];
    }
    // Try minor arcana
    return MINOR_LOOKUP[card.name_ru] || MINOR_LOOKUP[card.name] || null;
  };

  // Fan card positions (arc layout) - responsive
  const getFanCardStyle = (index) => {
    const totalAngle = isMobile ? 80 : 90;
    const startAngle = -totalAngle / 2;
    const angleStep = totalAngle / (TOTAL_FAN_CARDS - 1);
    const angle = startAngle + index * angleStep;
    const radius = isMobile ? 320 : 500;
    
    return {
      rotate: angle,
      x: Math.sin((angle * Math.PI) / 180) * radius * 0.55,
      y: -Math.cos((angle * Math.PI) / 180) * radius * 0.15,
    };
  };

  // Responsive card dimensions
  const fanCardW = isMobile ? 70 : 110;
  const fanCardH = isMobile ? 108 : 170;
  const pickedSpacing = isMobile ? 100 : 160;
  const pickedFloatY = isMobile ? -280 : -380;

  const isPicked = (index) => pickedIndices.includes(index);
  const pickOrder = (index) => pickedIndices.indexOf(index);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[#080510] flex flex-col items-center justify-center overflow-hidden"
      data-testid="tarot-card-picker"
    >
      {/* Mystical background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#D4AF37] rounded-full"
            initial={{ 
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000), 
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
              opacity: 0 
            }}
            animate={{ 
              y: [null, Math.random() * -200],
              opacity: [0, 0.6, 0],
            }}
            transition={{ 
              duration: 3 + Math.random() * 4, 
              repeat: Infinity, 
              delay: Math.random() * 3 
            }}
          />
        ))}
      </div>

      {/* Phase indicator */}
      <div className="absolute top-8 left-0 right-0 text-center z-20">
        <motion.p
          key={phase}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[#D4AF37]/70 text-sm tracking-[0.3em] uppercase font-heading"
        >
          {phase === PHASES.SHUFFLING && (t('tarotPicker.shuffling') || 'The cards are being shuffled...')}
          {phase === PHASES.FAN && (t('tarotPicker.focus') || 'Focus on your question...')}
          {phase === PHASES.PICKING && (
            pickedIndices.length < 3 
              ? `${t('tarotPicker.choose') || 'Choose a card'}: ${positionLabels[pickedIndices.length]} (${pickedIndices.length + 1}/3)`
              : (t('tarotPicker.chosen') || 'Cards chosen...')
          )}
          {phase === PHASES.REVEALING && (t('tarotPicker.revealing') || 'The cards reveal their wisdom...')}
        </motion.p>
      </div>

      {/* SHUFFLING PHASE */}
      <AnimatePresence>
        {phase === PHASES.SHUFFLING && (
          <motion.div 
            className="relative w-48 h-72"
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.4 }}
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute top-0 left-0 w-full h-full"
                initial={{ x: 0, y: 0, rotate: 0 }}
                animate={{
                  x: [0, (i % 2 === 0 ? 1 : -1) * (40 + i * 10), 0, (i % 2 === 0 ? -1 : 1) * (30 + i * 8), 0],
                  y: [0, -15, 0, 15, 0],
                  rotate: [0, (i % 2 === 0 ? 1 : -1) * (8 + i * 2), 0, (i % 2 === 0 ? -1 : 1) * (6 + i), 0],
                }}
                transition={{
                  duration: 1.2,
                  repeat: 1,
                  delay: i * 0.08,
                  ease: "easeInOut",
                }}
              >
                <div 
                  className="w-full h-full rounded-xl border border-[#D4AF37]/40 shadow-[0_0_20px_rgba(212,175,55,0.15)]"
                  style={{
                    backgroundImage: `url("${CARD_BACK}")`,
                    backgroundSize: 'cover',
                  }}
                />
              </motion.div>
            ))}
            {/* Glow effect */}
            <motion.div
              className="absolute -inset-8 rounded-3xl"
              animate={{
                boxShadow: [
                  '0 0 40px rgba(147, 51, 234, 0.2)',
                  '0 0 80px rgba(212, 175, 55, 0.3)',
                  '0 0 40px rgba(147, 51, 234, 0.2)',
                ],
              }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAN & PICKING PHASE */}
      {(phase === PHASES.FAN || phase === PHASES.PICKING) && (
        <div className="relative w-full flex items-end justify-center" style={{ height: '85vh', paddingBottom: isMobile ? '12vh' : '5vh' }}>
          <div className="relative" style={{ width: isMobile ? '100%' : '600px', height: '300px' }}>
            {[...Array(TOTAL_FAN_CARDS)].map((_, i) => {
              const style = getFanCardStyle(i);
              const picked = isPicked(i);
              const order = pickOrder(i);
              
              if (picked && phase === PHASES.PICKING) {
                const targetX = (order - 1) * pickedSpacing;
                return (
                  <motion.div
                    key={i}
                    className="absolute cursor-default"
                    style={{ left: '50%', bottom: 0, marginLeft: -fanCardW/2, width: fanCardW, height: fanCardH, zIndex: 30 + order }}
                    animate={{ 
                      x: targetX, 
                      y: pickedFloatY,
                      rotate: 0,
                      scale: isMobile ? 1.05 : 1.15,
                    }}
                    transition={{ type: 'spring', stiffness: 120, damping: 15 }}
                    data-testid={`picked-card-${order}`}
                  >
                    <div className="w-full h-full rounded-lg border-2 border-[#D4AF37] shadow-[0_0_25px_rgba(212,175,55,0.5)] relative overflow-hidden">
                      <div 
                        className="w-full h-full" 
                        style={{ backgroundImage: `url("${CARD_BACK}")`, backgroundSize: 'cover' }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 py-1 text-center">
                        <span className="text-[#D4AF37] text-[10px] tracking-widest uppercase font-heading">
                          {positionLabels[order]}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              }

              return (
                <motion.div
                  key={i}
                  className={`absolute ${phase === PHASES.PICKING ? 'cursor-pointer' : 'cursor-default'}`}
                  style={{ left: '50%', bottom: 0, marginLeft: -fanCardW/2, width: fanCardW, height: fanCardH, zIndex: i, transformOrigin: 'bottom center' }}
                  initial={{ x: 0, y: 100, rotate: 0, opacity: 0 }}
                  animate={{ 
                    x: style.x,
                    y: style.y,
                    rotate: style.rotate,
                    opacity: picked ? 0 : 1,
                  }}
                  whileHover={phase === PHASES.PICKING && !picked ? { 
                    y: style.y - 25, 
                    scale: 1.08,
                    zIndex: 20,
                    transition: { duration: 0.2 }
                  } : {}}
                  transition={{ 
                    type: 'spring', 
                    stiffness: 100, 
                    damping: 14, 
                    delay: phase === PHASES.FAN ? i * 0.04 : 0 
                  }}
                  onClick={() => handlePick(i)}
                  data-testid={`fan-card-${i}`}
                >
                  <div className={`w-full h-full rounded-lg border transition-all duration-200 ${
                    phase === PHASES.PICKING 
                      ? 'border-[#D4AF37]/40 hover:border-[#D4AF37] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]' 
                      : 'border-[#D4AF37]/20'
                  }`}>
                    <div 
                      className="w-full h-full rounded-lg" 
                      style={{ backgroundImage: `url("${CARD_BACK}")`, backgroundSize: 'cover' }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* REVEALING PHASE */}
      {phase === PHASES.REVEALING && (
        <div className="flex flex-col items-center gap-6 sm:gap-8 mt-8 px-4">
          <div className="flex items-start gap-3 sm:gap-6 md:gap-12">
            {[0, 1, 2].map((cardIdx) => {
              const card = apiResult?.cards?.[cardIdx];
              const isRevealed = revealedCards.includes(cardIdx);
              const artUrl = getCardArt(cardIdx);
              const revealW = isMobile ? 95 : 130;
              const revealH = isMobile ? 147 : 200;

              return (
                <motion.div 
                  key={cardIdx} 
                  className="flex flex-col items-center"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: cardIdx * 0.2 }}
                >
                  <span className="text-[#D4AF37]/60 text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase font-heading mb-2 sm:mb-3">
                    {positionLabels[cardIdx]}
                  </span>
                  <div 
                    className="relative" 
                    style={{ width: revealW, height: revealH, perspective: '800px' }}
                  >
                    <motion.div
                      className="w-full h-full relative"
                      style={{ transformStyle: 'preserve-3d' }}
                      animate={{ rotateY: isRevealed ? 180 : 0 }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    >
                      {/* Back */}
                      <div 
                        className="absolute inset-0 rounded-xl border-2 border-[#D4AF37]/60 shadow-[0_0_30px_rgba(212,175,55,0.3)]"
                        style={{ 
                          backfaceVisibility: 'hidden',
                          backgroundImage: `url("${CARD_BACK}")`, 
                          backgroundSize: 'cover',
                        }}
                      />
                      {/* Front */}
                      <div 
                        className="absolute inset-0 rounded-xl border-2 border-[#D4AF37] overflow-hidden shadow-[0_0_40px_rgba(212,175,55,0.5)]"
                        style={{ 
                          backfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                        }}
                      >
                        {artUrl ? (
                          <img src={artUrl} alt={card?.name || ''} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-b from-[#1a1035] to-[#0d0820] flex flex-col items-center justify-center p-3">
                            <span className="text-3xl mb-2">{card?.reversed ? '🔮' : '✨'}</span>
                            <span className="text-[#D4AF37] text-xs text-center font-heading leading-tight">
                              {card?.name_ru || card?.name || '?'}
                            </span>
                          </div>
                        )}
                        {card?.reversed && (
                          <div className="absolute top-1 right-1 bg-red-500/80 text-white text-[8px] px-1.5 py-0.5 rounded">
                            REV
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                  {/* Card name appears after reveal */}
                  <AnimatePresence>
                    {isRevealed && card && (
                      <motion.p
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-[#D4AF37]/80 text-[10px] sm:text-xs text-center mt-2 max-w-[95px] sm:max-w-[130px] font-heading"
                      >
                        {card.name_ru || card.name}
                        {card.reversed && <span className="text-red-400/70 block text-[10px]">
                          ({t('divination.reversed') || 'reversed'})
                        </span>}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Interpretation or loading */}
          <AnimatePresence>
            {showInterpretation && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-xl px-6 mt-4"
              >
                {apiResult ? (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={handleComplete}
                    className="w-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 text-[#D4AF37] 
                               py-4 rounded-xl font-heading tracking-[0.2em] uppercase text-sm
                               hover:bg-[#D4AF37]/20 transition-all active:scale-[0.98]
                               shadow-[0_0_30px_rgba(212,175,55,0.15)]"
                    data-testid="view-reading-btn"
                  >
                    {t('tarotPicker.viewReading') || 'View Full Reading'}
                  </motion.button>
                ) : apiError ? (
                  <div className="text-center">
                    <p className="text-red-400/80 text-sm mb-4">
                      {t('tarotPicker.error') || 'Something went wrong...'}
                    </p>
                    <button
                      onClick={() => onComplete(null, apiError)}
                      className="text-[#D4AF37]/70 text-sm underline"
                    >
                      {t('chat.back') || 'Back'}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                      className="w-6 h-6 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full"
                    />
                    <p className="text-[#D4AF37]/50 text-xs tracking-widest">
                      {t('tarotPicker.interpreting') || 'The oracle is reading the cards...'}
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Close button (small, top-right) */}
      <button
        onClick={() => onComplete(null, { cancelled: true })}
        className="absolute top-6 right-6 text-white/30 hover:text-white/60 transition-colors z-30 text-xl"
        data-testid="close-picker-btn"
      >
        &#10005;
      </button>
    </motion.div>
  );
};

export default TarotCardPicker;
